import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Home, Phone } from 'lucide-react';
import { Nav } from '../components/sections/Nav';
import { Footer } from '../components/sections/Footer';
import { useLenis } from '../lib/useLenis';

const PHONE = '(505) 589-3461';
const PHONE_HREF = 'tel:+15055893461';

const MONTSERRAT = "'Montserrat', ui-sans-serif, system-ui, sans-serif";
const SAND = '#F5F2EB';
const TAN = '#D2B48C';

const ThankYou = () => {
    useLenis();

    useEffect(() => {
        const prev = document.title;
        document.title = 'Estimate Request Received | MSD Painting Services';
        return () => { document.title = prev; };
    }, []);

    // Fire the Google Ads conversion EXACTLY ONCE, only for a genuine successful
    // estimate submission. Two valid success signals are supported:
    //   1) 'pending' flag set in Estimate.jsx when the embedded form posts a submit
    //      message to the parent page (in-app detection), or
    //   2) the form's own "redirect on submit" lands here with ?conversion=1
    //      (the most reliable method for the cross-origin Sentric/GHL form).
    // Refreshes, back/forward, direct visits and re-renders never fire it.
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const hasSuccessParam = params.get('conversion') === '1' || params.get('submitted') === '1';
            const pending = sessionStorage.getItem('msd:leadConversion') === 'pending';
            const navEntry = performance.getEntriesByType('navigation')[0];
            const isReload = navEntry && navEntry.type === 'reload';

            let shouldFire = false;
            if (pending) {
                // In-app detected submission (SPA navigation). Consume so it can't repeat.
                sessionStorage.setItem('msd:leadConversion', 'fired');
                shouldFire = true;
            } else if (hasSuccessParam && !isReload) {
                // Arrived via the form's redirect-on-submit. Fire once per arrival; a
                // refresh (isReload) or a repeat of the same URL is ignored.
                const token = 'u:' + window.location.pathname + window.location.search;
                if (sessionStorage.getItem('msd:convToken') !== token) {
                    sessionStorage.setItem('msd:convToken', token);
                    shouldFire = true;
                }
            }

            if (shouldFire && typeof window.gtag === 'function') {
                window.gtag('event', 'conversion', {
                    send_to: 'AW-18388129531/AArJCJahsPIcEPutksBE',
                });
            }
        } catch { /* storage/gtag unavailable */ }
    }, []);

    return (
        <div className="relative min-h-screen text-bone-100" style={{ backgroundColor: '#12100E' }} data-testid="thank-you-page">
            <Nav />

            <section className="relative min-h-screen flex items-center justify-center px-6 md:px-10 pt-28 pb-20">
                {/* Ambient warmth */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-40"
                    style={{ background: 'radial-gradient(ellipse at 50% 20%, rgba(210,180,140,0.14) 0%, rgba(210,180,140,0) 55%)' }}
                />

                <motion.div
                    initial={{ opacity: 0, y: 26 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
                    data-testid="thank-you-card"
                    className="relative w-full max-w-[680px] rounded-[24px] px-8 sm:px-12 py-14 md:py-16 text-center shadow-[0_40px_120px_rgba(0,0,0,0.55)]"
                    style={{ backgroundColor: '#1C1A17', border: `1px solid ${TAN}59` }}
                >
                    {/* Icon */}
                    <motion.div
                        initial={{ scale: 0.7, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.7, delay: 0.15, ease: [0.34, 1.56, 0.64, 1] }}
                        className="mx-auto mb-8 flex items-center justify-center w-16 h-16 rounded-full"
                        style={{ backgroundColor: `${TAN}1F`, border: `1px solid ${TAN}66` }}
                    >
                        <CheckCircle2 size={34} strokeWidth={1.75} style={{ color: TAN }} />
                    </motion.div>

                    {/* Accent bars */}
                    <div className="inline-flex items-center gap-1.5 mb-6">
                        <span className="block h-1 w-3 rounded-full" style={{ backgroundColor: TAN }} />
                        <span className="block h-1 w-9 rounded-full" style={{ backgroundColor: TAN }} />
                        <span className="block h-1 w-3 rounded-full" style={{ backgroundColor: TAN }} />
                    </div>

                    {/* Headline */}
                    <h1
                        data-testid="thank-you-headline"
                        className="font-extrabold tracking-[-0.01em] leading-[1.1]"
                        style={{ fontFamily: MONTSERRAT, color: SAND, fontSize: 'clamp(1.9rem, 4.6vw, 2.9rem)' }}
                    >
                        Estimate Request Received!
                    </h1>

                    {/* Body */}
                    <p
                        className="mx-auto mt-6 max-w-xl text-[15px] md:text-[16.5px] leading-relaxed"
                        style={{ fontFamily: MONTSERRAT, color: '#F5F2EBB3' }}
                    >
                        Thank you for reaching out to <span style={{ color: TAN }}>MSD Painting Services</span>. Our crew is reviewing your project details and will call or text you within 24 hours to schedule your free on-site walkthrough.
                    </p>

                    {/* CTAs */}
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/"
                            data-testid="thank-you-home-btn"
                            className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-4 rounded-full font-semibold text-[14px] tracking-wide transition-transform hover:-translate-y-0.5"
                            style={{ fontFamily: MONTSERRAT, backgroundColor: TAN, color: '#1C1A17' }}
                        >
                            <Home size={16} strokeWidth={2} />
                            Return to Homepage
                        </Link>
                        <a
                            href={PHONE_HREF}
                            data-testid="thank-you-call-btn"
                            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-4 rounded-full font-semibold text-[14px] tracking-wide transition-colors"
                            style={{ fontFamily: MONTSERRAT, color: SAND, border: `1.5px solid ${TAN}80`, backgroundColor: 'transparent' }}
                        >
                            <Phone size={15} strokeWidth={2} style={{ color: TAN }} />
                            Call Us Now: {PHONE}
                        </a>
                    </div>
                </motion.div>
            </section>

            <Footer />
        </div>
    );
};

export default ThankYou;
